import { useState, useEffect, useContext, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import classes from './StudioInfos.module.css';
import Dropdowns, { SHOW_FAKE_PAID_EQUIPMENT_SECTION } from './Dropdowns';
import { normalizePaidEquipmentInput } from './paidEquipmentHelpers';
import noUserImage from "../../public/fakeUser/noUser.webp"
import { useSelector } from 'react-redux';
import { ensureRedirectPath } from '../../util/redirectUtils';
import StudioServices from './StudioServices';
import rocketImage from "../../public/studioErstellen/rocketImage.webp";
import { buildCoarseLocationText } from '../../util/locationPrivacy';
import { trackHostContactStarted } from '../../util/tracking';

import { NavBarContext } from '../../App';

// import ChatLink from '../../components/ChatLink';

// import StudioFreelancers from './StudioFreelancers';

export const StudioInfos = ({ studio, reviews, userId, isPreviewMode = false, studioCreativesData, setShowBookingProcedure, setShowBookingPhone, isBookable = true }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showExpandButton, setShowExpandButton] = useState(false);
    const descriptionRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    const packageMinHours = Array.isArray(studio?.packages)
        ? studio.packages
            .map((pkg) => Number(pkg?.durationHours))
            .filter((hours) => Number.isFinite(hours) && hours > 0)
            .reduce((min, hours) => Math.min(min, hours), Infinity)
        : Infinity;

    const minBookableHours = studio?.pricingMode === "PACKAGES"
        ? (Number.isFinite(packageMinHours) ? packageMinHours : studio?.minNumberOfHours)
        : studio?.minNumberOfHours;

    // Use the passed studioCreativesData instead of making another query
    // This avoids duplicate queries and authorization issues
    const hasCreatives = studioCreativesData && studioCreativesData.length > 0;

    const { setShowSignIn, isPhoneSize } = useContext(NavBarContext);
    const { isAuthenticated } = useSelector((state) => state?.auth);
    const coarseLocationText = buildCoarseLocationText(studio);
    
    const calculateAverageRating = () => {
        if (!reviews.length) return "-";
        const total = reviews.reduce((sum, review) => sum + review.rating, 0);
        return (total / reviews.length).toFixed(1);
    };

    useEffect(() => {
        const checkDescriptionOverflow = () => {
            const descriptionElement = descriptionRef.current;
            if (!descriptionElement) {
                setShowExpandButton(false);
                return;
            }

            // Measure natural content height against the clamped 3-line height.
            const fullHeight = descriptionElement.scrollHeight;
            const clampedHeight = descriptionElement.clientHeight;
            setShowExpandButton(fullHeight > clampedHeight + 1);
        };

        setIsOpen(false);
        const animationFrame = window.requestAnimationFrame(checkDescriptionOverflow);
        window.addEventListener('resize', checkDescriptionOverflow);

        return () => {
            window.cancelAnimationFrame(animationFrame);
            window.removeEventListener('resize', checkDescriptionOverflow);
        };
    }, [studio?.description]);

    const handleMessageClick = () => {
        trackHostContactStarted({
            studio,
            source: 'studio_profile',
            requiresAuth: !isAuthenticated
        });

        if (!isAuthenticated && !isPhoneSize) {
            handleSignInClick();
            return;
        }else if  (!isAuthenticated && isPhoneSize) {
            const from = {
                pathname: location.pathname,
                search: location.search,
                hash: location.hash,
            };
            ensureRedirectPath(from);
        
            navigate('/anmelden', { state: { background: location, from, requiresAuth: true } });
            return
        }
        // Navigate to the message page
        navigate(`/studio/${studio.id}/nachricht`);
    };

    const toggleDescription = () => {
        setIsOpen(!isOpen);
    };

    const handleSignInClick = () => {
        setShowSignIn(true);
    };

    return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.titleContainer}>
            <h1 className={`${classes.title} titleSemibold2`}>{studio.StudioName}</h1>
            <div className={classes.originalContainer}>
                <div className={classes.originalIcon}>
                    <img src={rocketImage} alt="rocket" />
                </div>
                <span>stu original</span>
            </div>
        </div>
        <div className={`${classes.headerInfos} regularXl`}>
            <div className={classes.locationContainer}>
                <p className={`${classes.location} regularXl`}>{coarseLocationText}</p>
                <p className={`${classes.locationPhone} regularMiddle`}>{coarseLocationText}</p>
                {reviews?.length > 0 &&
                <svg className={classes.smallCircle} xmlns="http://www.w3.org/2000/svg" width="3" height="4" viewBox="0 0 3 4" fill="none">
                    <circle cx="1.5" cy="1.69336" r="1.5" fill="#1F1F1F"/>
                </svg>
                }
            </div>
            {reviews?.length > 0 &&
            <div className={classes.rating}>
                <span className={classes.ratingNumber}>{calculateAverageRating()}</span>
                <svg className={classes.star} xmlns="http://www.w3.org/2000/svg" width="19" height="18" viewBox="0 0 19 18" fill="none">
                    <g clipPath="url(#clip0_3947_4570)">
                        <path d="M14.2391 13.1393L14.9787 17.4738L9.40137 14.5568L13.9207 11.3008L14.1364 12.5436L14.2391 13.1393Z" fill="#1F1F1F"/>
                        <path d="M4.66741 12.5439L4.88311 11.3011L0.374023 6.93581L6.60868 6.03194L9.40246 0.423828L12.1962 6.03194L18.4309 6.93581L3.82517 17.4741L4.66741 12.5439Z" fill="#1F1F1F"/>
                    </g>
                    <defs>
                        <clipPath id="clip0_3947_4570">
                            <rect width="18.0569" height="17.0503" fill="white" transform="translate(0.374023 0.423828)"/>
                        </clipPath>
                    </defs>
                </svg>
                {reviews?.length > 0 && (
                    <span className={classes.reviews}>({reviews?.length} Bewertungen)</span>
                )}
            </div>
            }
        </div>
      </div>
      <div className={classes.horizontalLine}></div>
      <div className={classes.secondContainer}>
        <div className={classes.secondContainerInfos}>
            <div className={classes.infosBox}>
                <svg xmlns="http://www.w3.org/2000/svg" width="31" height="20" viewBox="0 0 31 20" fill="none">
                    <g clipPath="url(#clip0_3947_4564)">
                    <path d="M11.0979 5.88983C11.0979 5.09575 11.3001 4.35312 11.6541 3.70371C11.3918 3.5301 11.1105 3.38543 10.8103 3.27613C10.3931 4.05413 10.1561 4.94465 10.1561 5.88983C10.1561 7.6098 10.943 9.14973 12.1692 10.1496C12.3999 9.93416 12.6085 9.68983 12.7823 9.423C11.7552 8.59677 11.0979 7.32046 11.0979 5.88983ZM25.9136 18.5533C25.7999 15.9106 24.4978 13.5766 22.5384 12.0946C22.0801 11.7409 21.5871 11.4419 21.0656 11.1912C20.5568 10.9533 20.0259 10.7604 19.4697 10.6286C19.4697 10.6286 19.4686 10.6286 19.4665 10.6286C19.1441 10.5482 18.8123 10.4903 18.4741 10.4518C18.6069 10.3617 18.7333 10.2685 18.8565 10.1656C20.0986 9.17224 20.895 7.62266 20.895 5.88983C20.895 4.93822 20.6548 4.04449 20.2313 3.26327C20.0922 2.99965 19.9279 2.7521 19.7446 2.51741C18.7617 1.24753 17.2353 0.427734 15.5255 0.427734C13.8158 0.427734 12.2767 1.25396 11.2938 2.53349C11.1137 2.76496 10.9525 3.01572 10.8103 3.27613C10.3931 4.05413 10.1561 4.94465 10.1561 5.88983C10.1561 7.6098 10.943 9.14973 12.1692 10.1496C12.2988 10.2589 12.4347 10.3585 12.5737 10.4518C12.2103 10.4936 11.8563 10.5579 11.5087 10.6479C10.9872 10.7765 10.4816 10.9565 10.0044 11.1848C9.47343 11.4323 8.97409 11.7377 8.50952 12.0946C6.55009 13.5766 5.24802 15.9106 5.13425 18.5533C5.12793 18.6819 5.12477 18.8105 5.12477 18.9391V19.4213H25.9231V18.9391C25.9231 18.8105 25.92 18.6819 25.9136 18.5533ZM11.0979 5.88983C11.0979 5.09575 11.3001 4.35312 11.6541 3.70371C11.7963 3.44973 11.9575 3.21183 12.1439 2.99C12.953 2.00946 14.1697 1.38577 15.5255 1.38577C16.8813 1.38577 18.0823 2.00303 18.8913 2.97071C19.0778 3.19254 19.2421 3.43044 19.3843 3.68442C19.7478 4.33704 19.9532 5.08932 19.9532 5.88983C19.9532 7.33653 19.28 8.6257 18.2371 9.4455C18.0127 9.62553 17.7725 9.77985 17.5166 9.91166C16.9192 10.2203 16.2429 10.3939 15.5255 10.3939C14.8081 10.3939 14.1097 10.2139 13.5029 9.89559C13.2469 9.76378 13.0067 9.60303 12.7823 9.423C11.7552 8.59677 11.0979 7.32046 11.0979 5.88983ZM10.3204 12.0946C11.0884 11.7184 11.9354 11.4741 12.8234 11.3873C13.0541 11.368 13.288 11.3551 13.525 11.3551H17.5229C17.7473 11.3551 17.9685 11.3648 18.1897 11.3873C19.0904 11.4677 19.9469 11.712 20.7275 12.0946C23.1072 13.2487 24.7949 15.647 24.9687 18.4601H6.08236C6.25618 15.647 7.94381 13.2487 10.3204 12.0946Z" fill="#1F1F1F"/>
                    <path d="M24.2328 11.2749C25.557 10.3908 26.4324 8.86049 26.4324 7.12766C26.4324 4.39501 24.2423 2.16387 21.5528 2.16387C20.9144 2.16387 20.304 2.28916 19.7446 2.51741C19.4444 2.63958 19.1568 2.79068 18.8913 2.97071C19.0778 3.19254 19.2421 3.43044 19.3843 3.68442C19.6466 3.51081 19.9311 3.36936 20.2313 3.26327C20.6453 3.11538 21.0882 3.03511 21.5528 3.03511C23.7714 3.03511 25.5759 4.87402 25.5759 7.12766C25.5759 9.3813 23.7714 11.2234 21.5528 11.2234C21.3884 11.2234 21.2237 11.2137 21.0656 11.1912C20.4841 11.1237 19.9437 10.9243 19.4697 10.6286C19.4697 10.6286 19.4686 10.6286 19.4665 10.6286C19.2484 10.4968 19.043 10.3392 18.8565 10.1656C18.6227 9.95345 18.4141 9.70912 18.2371 9.4455C18.0127 9.62553 17.7725 9.77985 17.5166 9.91166C17.8737 10.4518 18.3355 10.9148 18.8696 11.2749C18.6389 11.3006 18.4109 11.3391 18.1897 11.3873C19.0904 11.4677 19.9469 11.712 20.7275 12.0946L23.3668 12.0947C26.9602 12.0947 29.9119 14.9527 30.1332 18.5534H12.9692C12.9724 18.5212 12.9724 18.4891 12.9787 18.4601H12.1191C12.1191 18.4923 12.1128 18.5212 12.1128 18.5534C12.1033 18.6981 12.1001 18.8427 12.1001 18.9874V19.4278H31.0023V18.9874C31.0023 15.0041 28.0378 11.7089 24.2328 11.2749Z" fill="#1F1F1F"/>
                    <path d="M18.8895 18.5534C18.8895 18.5212 18.8863 18.4923 18.8832 18.4601H18.0235C18.0299 18.4891 18.0299 18.5212 18.033 18.5534H0.8691C1.09349 14.9527 4.03894 12.0947 7.63228 12.0947H10.3186C11.0866 11.7185 11.9335 11.4742 12.8216 11.3874C12.594 11.3392 12.3665 11.3006 12.1326 11.2749C12.6762 10.9116 13.1439 10.4422 13.5011 9.89568C13.2451 9.76387 13.0049 9.60313 12.7805 9.42309C12.6067 9.68993 12.3981 9.93426 12.1674 10.1497C11.9651 10.3393 11.7439 10.5065 11.5069 10.648C11.0549 10.9212 10.5461 11.1109 10.0025 11.1849C9.82241 11.2106 9.63911 11.2234 9.45265 11.2234C7.23091 11.2234 5.42634 9.38773 5.42634 7.12766C5.42634 4.86759 7.23091 3.03511 9.45265 3.03511C9.92986 3.03511 10.385 3.11869 10.8084 3.27622C11.1087 3.38553 11.3899 3.5302 11.6523 3.7038C11.7945 3.44983 11.9557 3.21193 12.1421 2.9901C11.8766 2.81006 11.5922 2.65575 11.292 2.53358C10.7231 2.29568 10.1037 2.16387 9.45265 2.16387C6.76002 2.16387 4.57305 4.39501 4.57305 7.12766C4.57305 8.85727 5.44531 10.3876 6.7695 11.2749C2.96758 11.7089 0 15.0041 0 18.9874V19.4278H18.9021V18.9874C18.9021 18.8427 18.899 18.6981 18.8895 18.5534ZM12.1674 10.1497C11.9651 10.3393 11.7439 10.5065 11.5069 10.648C11.8545 10.5579 12.2085 10.4937 12.5719 10.4519C12.4329 10.3586 12.297 10.259 12.1674 10.1497Z" fill="#1F1F1F"/>
                    </g>
                    <defs>
                    <clipPath id="clip0_3947_4564">
                    <rect width="31" height="19" fill="white" transform="translate(0 0.427734)"/>
                    </clipPath>
                    </defs>
                </svg>
                <span>{studio?.numberOfPeople} Personen</span>
            </div>
            
            <div className={classes.infosBox}>
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="24" viewBox="0 0 22 24" fill="none">
                 <path fillRule="evenodd" clipRule="evenodd" d="M7.33025 0.173828L21.5 18.2382L14.6698 23.6738L0.5 5.60943L7.33025 0.173828ZM1.88451 5.77296L4.15914 8.67279L5.78897 7.37575L6.40005 8.15479L4.77022 9.45183L6.5511 11.7222L8.18224 10.4241L8.79332 11.2031L7.16218 12.5012L8.94399 14.7728L10.5755 13.4744L11.1866 14.2534L9.55507 15.5518L11.3376 17.8243L12.9683 16.5266L13.5794 17.3057L11.9487 18.6034L14.8321 22.2793L20.1155 18.0747L7.16789 1.56837L1.88451 5.77296Z" fill="#1F1F1F"/>
                </svg>
                <span>{studio?.studioSize}m²</span>
            </div>

            <div className={classes.infosBox}>
                <svg xmlns="http://www.w3.org/2000/svg" width="19" height="20" viewBox="0 0 19 20" fill="none">
                    <path d="M9.38623 4.67233V10.9719M9.16699 10.5629L13.8337 13.0732M18.5 9.92383C18.5 14.7563 14.5825 18.6738 9.75 18.6738C4.91751 18.6738 1 14.7563 1 9.92383C1 5.09134 4.91751 1.17383 9.75 1.17383C14.5825 1.17383 18.5 5.09134 18.5 9.92383Z" stroke="#1F1F1F" strokeMiterlimit="10"/>
                </svg>
                <span>Mind. {minBookableHours ?? "-"} Stunden</span>
            </div>
            
        </div>
            {/* <div className={classes.optionalLink}>
                <svg xmlns="http://www.w3.org/2000/svg" width="27" height="23" viewBox="0 0 27 23" fill="none">
                    <g clipPath="url(#clip0_3947_4544)">
                        <path d="M17.7464 9.85725C18.4582 8.96983 18.8846 7.83948 18.8846 6.6079C18.8846 3.7432 16.5746 1.42175 13.7241 1.42175C10.8736 1.42175 8.56358 3.7432 8.56358 6.6079C8.56358 7.83948 8.98999 8.96983 9.70178 9.85725M6.06495 13.7969C4.638 15.268 3.64417 17.3297 3.62402 20.1269H24.199C24.1788 17.333 23.185 15.2748 21.758 13.8036M21.6015 17.904L21.5612 18.9365C21.5612 19.5979 21.024 20.131 20.3693 20.131H7.45627C6.79819 20.131 6.26435 19.5945 6.26435 18.9365L6.22406 17.904L6.18712 16.9964L5.95881 11.0477C5.95881 10.3897 6.49602 9.85657 7.15074 9.85657H20.6715C21.3296 9.85657 21.8634 10.3931 21.8634 11.0477L21.6351 16.9964L21.5982 17.904H21.6015ZM15.2075 14.8186C15.2075 15.5379 14.6272 16.121 13.9115 16.121C13.1957 16.121 12.6154 15.5379 12.6154 14.8186C12.6154 14.0993 13.1957 13.5161 13.9115 13.5161C14.6272 13.5161 15.2075 14.0993 15.2075 14.8186Z" stroke="#1F1F1F" strokeMiterlimit="10"/>
                    </g>
                    <defs>
                        <clipPath id="clip0_3947_4544">
                            <rect width="26.5" height="22" fill="white" transform="translate(0 0.0957031)"/>
                        </clipPath>
                    </defs>
                </svg>
                <span>Fotograf/in optional buchbar</span>
            </div> */}
        </div>
        <div className={classes.horizontalLine}></div>
        <div className={classes.userProfileContainer}>
            <Link className={classes.userProfile} to={`/users/show/${studio.User.id}`}>
                <div className={classes.userPicture}>
                    <img src={studio?.User?.profilePicture === null ? noUserImage : studio?.User?.processedProfilePicture} alt='User Profile' />
                </div>
                <div className={classes.hostContainer}>
                    <div className={classes.host}>
                        <span style={{color: "var(--dark-grey)"}} className='titleSemibold3'>Studio Host: {studio.User.firstName}</span>
                    </div>
                    {/* <span className='regularMiddle' style={{color: "#696F76"}}>
                        Host/Fotografin
                    </span> */}
                </div>
            </Link>
            <div className={classes.buttonsContainer}>
                {userId !== studio?.User?.id && isBookable && (
                    <button 
                        className={`${classes.contactButton} mediumLarge`}
                        onClick={isPreviewMode ? undefined : handleMessageClick}
                        disabled={isPreviewMode}
                        style={{
                            cursor: isPreviewMode ? 'not-allowed' : 'pointer',
                            opacity: isPreviewMode ? 0.5 : 1
                        }}
                    >
                        Nachricht
                    </button>
                )}
                {!isPreviewMode ? (
                    <Link to={`/users/show/${studio.User.id}`} className={`${classes.profileButton} semiboldXs`}>
                        Profil anschauen
                    </Link>
                ) : (
                    <span className={`${classes.profileButton} semiboldXs`} style={{cursor: 'auto'}}>
                        Profil anschauen
                    </span>
                )}
            </div>
        </div>
        <div className={classes.horizontalLine}></div>
        <div className={classes.descriptionContainer}>
            <h3 className='titleSemibold3'>Beschreibung</h3>
            <p
                ref={descriptionRef}
                className={`${classes.dropdownContent} ${isOpen ? classes.open : ""} ${showExpandButton ? classes.dropdownContentClickable : ""}`}
                onClick={showExpandButton ? toggleDescription : undefined}
            >
                {studio?.description}
            </p>
            {showExpandButton && (
                <button type="button" className={classes.toggleButton} onClick={toggleDescription}>
                    {isOpen ? 'Weniger lesen' : 'Weiterlesen'}
                </button>
            )}
        </div>
        {studio?.amenities?.length > 0 && (
            <div className={classes.horizontalLine}></div>
        )}
        {studio?.amenities?.length > 0 && (
            <div className={classes.gruendAusstattung}>
                <h3 className='titleSemibold3'>Grundaustattung</h3>
                <div className={classes.gruendAusstattungItems}>
                {studio?.amenities?.map((amenity, index) => (
                    <div className={classes.gruendAusstattungItem} key={index}>
                        <span className='regularMiddle'>{amenity}</span>
                    </div>
                ))}
                </div>
                {/* <button className={classes.allViewButton}>Alle anschauen</button> */}
            </div>
        )}
        <div className={classes.horizontalLine}></div>
        {((studio?.equipment?.items?.length ?? 0) > 0 ||
            normalizePaidEquipmentInput(studio?.optionalBookableEquipment).length > 0 ||
            !!studio?.houseRules ||
            (typeof studio?.cancellationPolicy === "string" && studio.cancellationPolicy.trim().length > 0) ||
            SHOW_FAKE_PAID_EQUIPMENT_SECTION) && (
            <Dropdowns
                studioId={studio?.id}
                equipment={studio?.equipment?.items}
                paidEquipment={studio?.optionalBookableEquipment}
                houseRules={studio?.houseRules}
                cancellationPolicy={studio?.cancellationPolicy}
            />
        )}
        {/* <StudioFreelancers /> */}
        {hasCreatives && isBookable && (
            <StudioServices 
                studioCreativesData={studioCreativesData}
                studioId={studio?.id}
                userId={studio?.User?.id}
                processedProfilePicture={studio?.User?.processedProfilePicture}
                firstName={studio?.User?.firstName}
                setShowBookingProcedure={setShowBookingProcedure}
                setShowBookingPhone={setShowBookingPhone}
            />
        )}
        {hasCreatives && isBookable && (
            <div className={classes.horizontalLine}></div>
        )}
    </div>
  );
};

export default StudioInfos;
